import DefaultScreen from "@components/Global/DefaultScreenView";
import { Title } from "@constants/Themed";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import globalStyles from "@stylesheet";
import LottieView from "@utils/lottieAnimation";
import { FlatList, SectionList, Platform, Alert, TextInput, View, Pressable, ActivityIndicator, TouchableOpacity, StyleSheet } from "react-native";
import { XStack, Card, Text, YStack, Separator, H2, Paragraph, Popover, SizableText } from "tamagui";
import CourseItem from "./Components/CourseItem";
import LinkGenModal from '../DataScreen/CaptureScreen/Components/LinkGenModal';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { MaterialIcons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { HOST } from '@api/constant';
import { useQuery } from '@tanstack/react-query';
import useGlobalUser from '@hooks/getGlobalUser';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { AcademyStackParamList } from '@navigation/types';
import {
  getPublicAcademyCourses,
  getAcademyCourses,
  searchAcademyCourses,
  voteAcademyCourse
} from '@api/api';

type AcademyMainNavigationProp = StackNavigationProp<AcademyStackParamList, 'AcademyMain'>;

const AcademyScreen = () => {
  const theme = themeSelector();
  const currentUserId = useGlobalUser();
  const navigation = useNavigation<AcademyMainNavigationProp>();
  const [searchQuery, setSearchQuery] = useState('');
  const linkGenModalRef = useRef(null);

  // Sorting state
  const [sortBy, setSortBy] = useState<'upvotes' | 'dateAdded' | 'title' | 'voteScore' | 'completion'>('upvotes');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showSortModal, setShowSortModal] = useState(false);
  const [headerOpen, setHeaderOpen] = useState(true);

  // Map UI sort options to backend field names used by the public courses API
  const mapSortByToApiField = useCallback((value: typeof sortBy) => {
    switch (value) {
      case 'upvotes':
        return 'voting.upvotes';
      case 'voteScore':
        return 'voting.voteScore';
      case 'dateAdded':
        return 'createdAt';
      case 'title':
        return 'title';
      case 'completion':
        return 'completionCount';
      default:
        return 'createdAt';
    }
  }, []);

  // Voting state
  const [courseVoting, setCourseVoting] = useState<Record<string, {
    voting: {
      upvotes: number;
      downvotes: number;
      totalVotes: number;
      voteScore: number;
      voteRatio: number;
    };
    userVote: 'upvote' | 'downvote' | null;
  }>>({});

  // Fetch public courses using useQuery
  const {
    data: publicCoursesData,
    isLoading: publicLoading,
    error: publicError,
    refetch: refetchPublic
  } = useQuery({
    queryKey: ['academy-public-courses', sortBy, sortOrder],
    queryFn: async () => {
      console.log('📚 Fetching public courses...');
      const response = await getPublicAcademyCourses({
        page: 1,
        limit: 20,
        // Note: isPublic and isPublished are automatically set to true by the backend for public routes
        sortBy: mapSortByToApiField(sortBy),
        sortOrder: sortOrder
      });
      console.log('📚 Public courses response:', response);
      console.log('📚 Public courses count:', response?.courses?.length || 0);
      return response;
    },
    retry: 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Fetch user's courses using useQuery
  const {
    data: myCoursesData,
    isLoading: myLoading,
    error: myError,
    refetch: refetchMy
  } = useQuery({
    queryKey: ['academy-my-courses', currentUserId, sortBy, sortOrder],
    queryFn: async () => {
      console.log('👤 Fetching user courses for user:', currentUserId);
      const response = await getAcademyCourses({
        page: 1,
        limit: 20,
        author: currentUserId, // Filter by current user
        sortBy: mapSortByToApiField(sortBy),
        sortOrder: sortOrder
      });
      console.log('👤 User courses response:', response);
      return response;
    },
    enabled: !!currentUserId, // Only fetch when we have a user ID
    retry: 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Search courses using useQuery
  const {
    data: searchData,
    isLoading: searchLoading,
    error: searchError,
    refetch: refetchSearch
  } = useQuery({
    queryKey: ['academy-search', searchQuery, sortBy, sortOrder],
    queryFn: async () => {
      if (!searchQuery.trim()) return null;

      console.log('🔍 Searching courses with query:', searchQuery);
      const response = await searchAcademyCourses({
        q: searchQuery,
        sortBy: sortBy === 'upvotes' ? 'popular' : sortBy === 'dateAdded' ? 'newest' : 'relevance',
        page: 1,
        limit: 20
      });
      console.log('🔍 Search response:', response);
      return response;
    },
    enabled: !!searchQuery.trim(),
    retry: 2,
    staleTime: 1000 * 60 * 2, // 2 minutes for search
  });

  // Combined loading and error states
  const isLoading = publicLoading || myLoading || searchLoading;
  const hasError = publicError || myError || searchError;

  // Refetch courses when screen comes into focus (e.g., after creating a new course)
  useFocusEffect(
    useCallback(() => {
      refetchPublic();
      refetchMy();
    }, [refetchPublic, refetchMy])
  );

  // Modern Section Header Component with Glass Effect
  const ModernSectionHeader = useCallback(({ title, isOpen, onToggle, count }: { title: string, isOpen: boolean, onToggle: () => void, count?: number }) => (
    <Animatable.View
      animation="fadeInDown"
      duration={300}
      style={Platform.OS === 'web'
        ? { ...globalStyles.modernCardSmall, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), marginVertical: 8, marginHorizontal: 20, ...styles.glassCardSmall }
        : [globalStyles.modernCardSmall, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { marginVertical: 8, marginHorizontal: 20 }]
      }
    >
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 12
      }}>
        <TouchableOpacity onPress={onToggle} style={{ flex: 1 }}>
          <View>
            <Text style={{
              color: theme ? '#1e293b' : '#f1f5f9',
              fontWeight: '700',
              fontSize: 18
            }}>
              {title}
            </Text>
            {count !== undefined && (
              <Text style={{
                color: theme ? '#64748b' : '#94a3b8',
                fontSize: 12,
                marginTop: 2
              }}>
                {count} courses
              </Text>
            )}
          </View>
        </TouchableOpacity>

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <TouchableOpacity onPress={onToggle}>
            <MaterialIcons
              name={isOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
              size={24}
              color={theme ? '#64748b' : '#94a3b8'}
            />
          </TouchableOpacity>
        </View>
      </View>
    </Animatable.View>
  ), [theme]);

  // Voting handler
  const handleVote = useCallback(async (courseId: string, voteType: 'upvote' | 'downvote') => {
    try {
      // Optimistic update
      setCourseVoting(prev => ({
        ...prev,
        [courseId]: {
          ...prev[courseId],
          voting: prev[courseId]?.voting || {
            upvotes: 0,
            downvotes: 0,
            totalVotes: 0,
            voteScore: 0,
            voteRatio: 0
          },
          userVote: prev[courseId]?.userVote === voteType ? null : voteType
        }
      }));

      // Make API call
      const response = await voteAcademyCourse(courseId, voteType);

      // Update with actual response
      setCourseVoting(prev => ({
        ...prev,
        [courseId]: {
          voting: response.voting,
          userVote: response.userVote
        }
      }));
    } catch (error) {
      console.error('Error voting on course:', error);
      Alert.alert('Voting Error', 'Failed to submit your vote. Please try again.');

      // Revert optimistic update on error
      setCourseVoting(prev => ({
        ...prev,
        [courseId]: prev[courseId] || {
          voting: {
            upvotes: 0,
            downvotes: 0,
            totalVotes: 0,
            voteScore: 0,
            voteRatio: 0
          },
          userVote: null
        }
      }));
    }
  }, []);

  // Transform API data to match component structure
  const courseData = useMemo(() => {
    const transformCourse = (course: any) => ({
      _id: course._id,
      completed: course.completed || false,
      title: course.title,
      description: course.description,
      category: course.category,
      level: course.level,
      duration: course.estimatedDuration ? `${Math.round(course.estimatedDuration)} min` : 'Unknown',
      isPublic: course.isPublic,
      author: course.author?.profile_information?.name || course.author?.email || 'Unknown',
      authorId: course.author?._id || course.author,
      voting: course.voting || { upvotes: 0, downvotes: 0, totalVotes: 0, voteScore: 0, voteRatio: 0 },
      dateAdded: new Date(course.createdAt || course.dateAdded),
      modules: course.modules || []
    });

    // Use search data if searching, otherwise show public and my courses
    if (searchQuery.trim() && searchData) {
      return [
        {
          title: "Search Results",
          subtitle: `Found ${searchData.courses?.length || 0} courses matching "${searchQuery}"`,
          data: (searchData.courses || []).map(transformCourse)
        }
      ];
    }

    // Safely access courses from API response
    const publicCourses = Array.isArray(publicCoursesData?.courses) ? publicCoursesData.courses : [];
    const myCourses = Array.isArray(myCoursesData?.courses) ? myCoursesData.courses : [];

    return [
      {
        title: "Public Courses",
        subtitle: "Free courses available to everyone",
        data: publicCourses.map(transformCourse)
      },
      {
        title: "My Courses",
        subtitle: "Your custom-built courses and content",
        data: myCourses.map(transformCourse)
      }
    ];
  }, [publicCoursesData, myCoursesData, searchData, searchQuery]);

  // Sorting function
  const sortCourses = useCallback((courses: any[], sortBy: string, sortOrder: 'asc' | 'desc') => {
    return [...courses].sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case 'upvotes':
          aValue = a.voting?.upvotes || 0;
          bValue = b.voting?.upvotes || 0;
          break;
        case 'dateAdded':
          aValue = new Date(a.dateAdded).getTime();
          bValue = new Date(b.dateAdded).getTime();
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'voteScore':
          aValue = a.voting?.voteScore || 0;
          bValue = b.voting?.voteScore || 0;
          break;
        case 'completion':
          aValue = a.completed ? 1 : 0;
          bValue = b.completed ? 1 : 0;
          break;
        default:
          aValue = a.voting?.upvotes || 0;
          bValue = b.voting?.upvotes || 0;
      }

      if (sortBy === 'title') {
        return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });
  }, []);

  // Filter and sort courses
  const filteredCourseData = useMemo(() => {
    // Note: search filtering is already handled by the search API in courseData useMemo
    // So we only need to apply sorting here
    return courseData.map(section => ({
      ...section,
      data: sortCourses(section.data, sortBy, sortOrder)
    }));
  }, [courseData, sortBy, sortOrder, sortCourses]);
  // Loading state component with Glass Effect
  const LoadingStateComponent = useCallback(() => (
    <Animatable.View
      animation="fadeIn"
      duration={300}
      style={Platform.OS === 'web'
        ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), alignItems: 'center', padding: 24, marginHorizontal: 20, ...styles.glassCard }
        : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { alignItems: 'center', padding: 24, marginHorizontal: 20 }]
      }
    >
      <View style={{ alignItems: 'center', gap: 16 }}>
        <ActivityIndicator size="large" color={theme ? "#3b82f6" : "#60a5fa"} />
        <Text style={{
          color: theme ? '#1e293b' : '#f1f5f9',
          fontSize: 16,
          fontWeight: '500'
        }}>
          {searchQuery ? 'Searching courses...' : 'Loading courses...'}
        </Text>
      </View>
    </Animatable.View>
  ), [theme, searchQuery]);

  // Error state component with Glass Effect
  const ErrorStateComponent = useCallback(() => {
    const errorMessage = publicError?.message || myError?.message || searchError?.message || 'Failed to load courses';
    return (
      <Animatable.View
        animation="fadeIn"
        duration={300}
        style={Platform.OS === 'web'
          ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), alignItems: 'center', padding: 24, marginHorizontal: 20, ...styles.glassCard }
          : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { alignItems: 'center', padding: 24, marginHorizontal: 20 }]
        }
      >
        <View style={{ alignItems: 'center', gap: 16 }}>
          <MaterialIcons name="error-outline" size={48} color="#f44336" />
          <Text style={{ color: '#f44336', fontSize: 16, fontWeight: '600', textAlign: 'center' }}>
            {errorMessage}
          </Text>
          <TouchableOpacity
            style={Platform.OS === 'web'
              ? { ...globalStyles.modernActionButton, ...(theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark), paddingHorizontal: 24, paddingVertical: 12 }
              : [globalStyles.modernActionButton, theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark, { paddingHorizontal: 24, paddingVertical: 12 }]
            }
            onPress={() => {
              refetchPublic();
              refetchMy();
              if (searchQuery) refetchSearch();
            }}
          >
            <Text style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>Retry</Text>
          </TouchableOpacity>
        </View>
      </Animatable.View>
    );
  }, [theme, publicError, myError, searchError, searchQuery, refetchPublic, refetchMy, refetchSearch]);

  // Loading state
  if (isLoading && !publicCoursesData && !myCoursesData) {
    return (
      <DefaultScreen scrollDisabled containerStyle={{ marginTop: 0, paddingTop: 0, paddingBottom: 140 }} style={{ paddingTop: 0 }} noMargin>
        <LoadingStateComponent />
      </DefaultScreen>
    );
  }

  // Error state
  if (hasError && !publicCoursesData && !myCoursesData) {
    return (
      <DefaultScreen scrollDisabled containerStyle={{ marginTop: 0, paddingTop: 0, paddingBottom: 140 }} style={{ paddingTop: 0 }} noMargin>
        <ErrorStateComponent />
      </DefaultScreen>
    );
  }

  return (
    <DefaultScreen scrollDisabled containerStyle={{ marginTop: 0, paddingTop: 10, paddingBottom: 140 }} style={{ paddingTop: 0 }} noMargin>
      <YStack flex={1} backgroundColor="transparent" gap="$2">
      

        <Animatable.View
          useNativeDriver
          transition={'height'}
          direction='alternate'
          style={{ overflow: 'hidden' }}
        >
          {headerOpen && (
            <Animatable.View
              animation="fadeInUp"
              duration={400}
              style={Platform.OS === 'web'
                ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), marginHorizontal: 20, marginBottom: 10, ...styles.glassCard }
                : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { marginHorizontal: 20, marginBottom: 10 }]
              }
            >
              <YStack padding={20} gap={16}>
              

                {/* Animated Icon - Only on Native */}
                {Platform.OS !== 'web' && (
                  <XStack justifyContent="center" marginBottom={8}>
                    <LottieView
                      source={require("@assets/animations/graduation.json")}
                      autoPlay
                      loop
                      style={{ width: 80, height: 80 }}
                    />
                  </XStack>
                )}

                {/* Search Section */}
                <YStack>
                  <XStack alignItems="center" marginBottom={8}>
                    <MaterialIcons name="search" size={18} color={theme ? "#3b82f6" : "#60a5fa"} />
                    <Text style={{
                      fontSize: 16,
                      fontWeight: '600',
                      color: theme ? "#1e293b" : "#f1f5f9",
                      marginLeft: 8
                    }}>
                      Search Courses
                    </Text>
                  </XStack>
                  <XStack
                    alignItems="center"
                    backgroundColor={theme ? "rgba(248, 250, 252, 0.8)" : "rgba(51, 65, 85, 0.8)"}
                    borderRadius={12}
                    paddingHorizontal={16}
                    paddingVertical={12}
                    borderWidth={1}
                    borderColor={theme ? "rgba(226, 232, 240, 0.5)" : "rgba(71, 85, 105, 0.5)"}
                    style={Platform.OS === 'web' ? { backdropFilter: 'blur(10px)' } : {}}
                  >
                    <MaterialIcons
                      name="search"
                      size={20}
                      color={theme ? "#64748b" : "#94a3b8"}
                      style={{ marginRight: 12 }}
                    />
                    <TextInput
                      style={{
                        flex: 1,
                        fontSize: 16,
                        color: theme ? "#1e293b" : "#f1f5f9",
                        paddingVertical: 4,
                      }}
                      placeholder="Search by title, description, or category..."
                      placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                      value={searchQuery}
                      onChangeText={setSearchQuery}
                      autoCapitalize="none"
                      autoCorrect={false}
                    />
                    {searchQuery.length > 0 && (
                      <TouchableOpacity onPress={() => setSearchQuery('')}>
                        <MaterialIcons
                          name="close"
                          size={20}
                          color={theme ? "#64748b" : "#94a3b8"}
                          style={{ marginLeft: 8 }}
                        />
                      </TouchableOpacity>
                    )}
                  </XStack>
                  {searchQuery.length > 0 && (
                    <XStack alignItems="center" marginTop={8} gap={6}>
                      <MaterialIcons
                        name="info-outline"
                        size={14}
                        color={theme ? "#64748b" : "#94a3b8"}
                      />
                      <Text style={{
                        fontSize: 12,
                        color: theme ? "#64748b" : "#94a3b8",
                        fontStyle: 'italic'
                      }}>
                        {searchLoading ? 'Searching...' : `${filteredCourseData[0]?.data?.length || 0} course(s) found`}
                      </Text>
                    </XStack>
                  )}
                </YStack>

                {/* Sort/Filter Section */}
                <YStack>
                  <XStack alignItems="center" marginBottom={8}>
                    <MaterialIcons name="sort" size={18} color={theme ? "#3b82f6" : "#60a5fa"} />
                    <Text style={{
                      fontSize: 16,
                      fontWeight: '600',
                      color: theme ? "#1e293b" : "#f1f5f9",
                      marginLeft: 8
                    }}>
                      Sort & Filter
                    </Text>
                  </XStack>
                  <XStack alignItems="center" gap={8} flexWrap="wrap">
                    {/* Sort By Button */}
                    <TouchableOpacity
                      onPress={() => setShowSortModal(true)}
                      style={Platform.OS === 'web'
                        ? { ...globalStyles.modernActionButton, ...(theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark), flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, minWidth: 130 }
                        : [globalStyles.modernActionButton, theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark, { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, minWidth: 130 }]
                      }
                    >
                      <MaterialIcons name="filter-list" size={16} color="white" style={{ marginRight: 6 }} />
                      <Text style={{ fontSize: 13, color: 'white', flex: 1, fontWeight: '600' }}>
                        {sortBy === 'upvotes' ? 'Upvotes' :
                          sortBy === 'dateAdded' ? 'Date' :
                            sortBy === 'title' ? 'Title' :
                              sortBy === 'voteScore' ? 'Score' :
                                sortBy === 'completion' ? 'Progress' : 'Upvotes'}
                      </Text>
                      <MaterialIcons name="expand-more" size={16} color="white" />
                    </TouchableOpacity>

                    {/* Sort Order Toggle */}
                    <TouchableOpacity
                      onPress={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                      style={Platform.OS === 'web'
                        ? { ...globalStyles.modernActionButton, ...(theme ? globalStyles.modernActionGreen : globalStyles.modernActionGreenDark), flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, minWidth: 90 }
                        : [globalStyles.modernActionButton, theme ? globalStyles.modernActionGreen : globalStyles.modernActionGreenDark, { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, minWidth: 90 }]
                      }
                    >
                      <MaterialIcons
                        name={sortOrder === 'desc' ? "arrow-downward" : "arrow-upward"}
                        size={16}
                        color="white"
                        style={{ marginRight: 6 }}
                      />
                      <Text style={{ fontSize: 13, color: 'white', fontWeight: '600' }}>
                        {sortOrder === 'desc' ? 'Desc' : 'Asc'}
                      </Text>
                    </TouchableOpacity>

                    {/* Reset Sort */}
                    <TouchableOpacity
                      onPress={() => {
                        setSortBy('upvotes');
                        setSortOrder('desc');
                      }}
                      style={Platform.OS === 'web'
                        ? { ...globalStyles.modernActionButton, minWidth: 100, backgroundColor: theme ? "#94a3b8" : "#475569", flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8 }
                        : [globalStyles.modernActionButton, { backgroundColor: theme ? "#94a3b8" : "#475569", flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8 }]
                      }
                    >
                      <MaterialIcons name="refresh" size={16} color="white" style={{ marginRight: 4 }} />
                      <Text style={{ fontSize: 13, color: 'white', fontWeight: '600' }}>
                        Reset
                      </Text>
                    </TouchableOpacity>
                  </XStack>
                </YStack>
              </YStack>
            </Animatable.View>
          )}
        </Animatable.View>
        {/* Course List Section */}
        <YStack flex={1}>
          {isLoading && (publicCoursesData?.courses?.length > 0 || myCoursesData?.courses?.length > 0) && (
            <View style={Platform.OS === 'web'
              ? { ...globalStyles.modernCardSmall, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), marginHorizontal: 20, padding: 12 }
              : [globalStyles.modernCardSmall, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { marginHorizontal: 20, padding: 12 }]
            }>
              <XStack alignItems="center" gap={12} justifyContent="center">
                <ActivityIndicator size="small" color={theme ? "#3b82f6" : "#60a5fa"} />
                <Text style={{
                  fontSize: 14,
                  color: theme ? "#64748b" : "#94a3b8",
                  fontWeight: '500'
                }}>
                  {searchQuery ? 'Searching...' : 'Loading...'}
                </Text>
              </XStack>
            </View>
          )}
          <SectionList
            renderSectionHeader={({ section }) => {
              const { title, subtitle } = section;
              const isPublic = title === "Public Courses";
              const isInternal = title === "My Courses";

              return (
                <Animatable.View
                  animation="fadeInUp"
                  duration={300}
                  delay={100}
                  style={Platform.OS === 'web'
                    ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), marginHorizontal: 20, marginTop: 12, padding: 20, ...styles.glassCard }
                    : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { marginHorizontal: 20, marginTop: 12, padding: 20 }]
                  }
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                      <View style={{
                        backgroundColor: isPublic
                          ? (theme ? "#dbeafe" : "#1e3a8a")
                          : (theme ? "#fef3c7" : "#78350f"),
                        borderRadius: 12,
                        padding: 10,
                        marginRight: 12
                      }}>
                        <MaterialIcons
                          name={isPublic ? "public" : "person"}
                          size={18}
                          color={isPublic
                            ? (theme ? "#1e40af" : "#60a5fa")
                            : (theme ? "#d97706" : "#fbbf24")}
                        />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={{
                          fontSize: 18,
                          fontWeight: '700',
                          color: theme ? "#1e293b" : "#f1f5f9",
                          marginBottom: 4
                        }}>
                          {title || ""}
                        </Text>
                        <Text style={{
                          fontSize: 13,
                          color: theme ? "#64748b" : "#94a3b8"
                        }}>
                          {subtitle || ""}
                        </Text>
                      </View>
                    </View>
                    <TouchableOpacity
                      onPress={() => {
                        if (isInternal) {
                          // Navigate to the course editor for creating a new course
                          navigation.navigate('CourseEditor', {
                            courseId: 'new-course',
                            courseTitle: 'New Course',
                            mode: 'edit'
                          });
                        } else {
                          console.log("Public courses action");
                        }
                      }}
                      style={Platform.OS === 'web'
                        ? { ...globalStyles.modernActionButton, ...(isPublic ? (theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark) : (theme ? globalStyles.modernActionAmber : globalStyles.modernActionAmberDark)), padding: 10, borderRadius: 10 }
                        : [globalStyles.modernActionButton, isPublic ? (theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark) : (theme ? globalStyles.modernActionAmber : globalStyles.modernActionAmberDark), { padding: 10, borderRadius: 10 }]
                      }
                    >
                      <MaterialIcons
                        name={isPublic ? "explore" : "add-circle"}
                        size={18}
                        color="white"
                      />
                    </TouchableOpacity>
                  </View>
                </Animatable.View>
              )
            }}
            contentContainerStyle={{ paddingBottom: 30 }}
            SectionSeparatorComponent={() => <View style={{ height: 8 }} />}
            renderSectionFooter={({ section }) => {
              const completedCount = section.data.filter(item => item.completed).length;
              const totalCount = section.data.length;
              const isPublic = section.title === "Public Courses";

              return (
                <View style={Platform.OS === 'web'
                  ? { ...globalStyles.modernCardSmall, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), marginHorizontal: 20, marginTop: 8, marginBottom: 16, paddingHorizontal: 16, paddingVertical: 12, ...styles.glassCardSmall }
                  : [globalStyles.modernCardSmall, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { marginHorizontal: 20, marginTop: 8, marginBottom: 16, paddingHorizontal: 16, paddingVertical: 12 }]
                }>
                  <XStack alignItems="center" gap={12} flexWrap="wrap">
                    <XStack alignItems="center" gap={6}>
                      <MaterialIcons
                        name="check-circle"
                        size={16}
                        color={theme ? "#10b981" : "#34d399"}
                      />
                      <Text style={{
                        fontWeight: '600',
                        fontSize: 13,
                        color: theme ? "#10b981" : "#34d399"
                      }}>
                        {completedCount} completed
                      </Text>
                    </XStack>

                    <View style={{ width: 1, height: 14, backgroundColor: theme ? "#e2e8f0" : "#475569" }} />

                    <XStack alignItems="center" gap={6}>
                      <MaterialIcons
                        name="menu-book"
                        size={16}
                        color={theme ? "#64748b" : "#94a3b8"}
                      />
                      <Text style={{
                        fontWeight: '600',
                        fontSize: 13,
                        color: theme ? "#64748b" : "#94a3b8"
                      }}>
                        {totalCount} total
                      </Text>
                    </XStack>

                    <View style={{ width: 1, height: 14, backgroundColor: theme ? "#e2e8f0" : "#475569" }} />

                    {isPublic ? (
                      <XStack alignItems="center" gap={6}>
                        <MaterialIcons
                          name="public"
                          size={16}
                          color={theme ? "#3b82f6" : "#60a5fa"}
                        />
                        <Text style={{
                          fontWeight: '600',
                          fontSize: 13,
                          color: theme ? "#3b82f6" : "#60a5fa"
                        }}>
                          Free access
                        </Text>
                      </XStack>
                    ) : (
                      <XStack alignItems="center" gap={6}>
                        <MaterialIcons
                          name="edit"
                          size={16}
                          color={theme ? "#d97706" : "#fbbf24"}
                        />
                        <Text style={{
                          fontWeight: '600',
                          fontSize: 13,
                          color: theme ? "#d97706" : "#fbbf24"
                        }}>
                          Your content
                        </Text>
                      </XStack>
                    )}
                  </XStack>
                </View>
              )
            }}
            renderItem={({ section, item, index }) => {
              // Render horizontal FlatList only for the first item of each section
              if (index === 0) {
                return (
                  <FlatList
                    data={section.data}
                    horizontal
                    style={{ marginVertical: 10 }}
                    contentContainerStyle={{ paddingHorizontal: 20, gap: 20 }}
                    showsHorizontalScrollIndicator={false}
                    keyExtractor={(item, index) => `${item._id || item.title}-${index}`}
                    renderItem={({ item, index }) => {
                      return (
                        <CourseItem
                          id={item._id || ""}
                          completed={item.completed}
                          title={item.title}
                          description={item.description}
                          index={index}
                          duration={item.duration}
                          level={item.level}
                          category={item.category}
                          isPublic={item.isPublic}
                          author={item.author}
                          authorId={item.authorId}
                          currentUserId={currentUserId}
                          courseData={item}
                          onShareCourse={(courseData) => {
                            // Modify course data to have empty title so it doesn't affect URL
                            const modifiedCourseData = { ...courseData, title: '' };
                            linkGenModalRef.current?.openModal(modifiedCourseData);
                          }}
                          voting={courseVoting[item._id]?.voting || item.voting}
                          userVote={courseVoting[item._id]?.userVote || null}
                          onVote={handleVote}
                        />
                      )
                    }}
                  />
                );
              }
              // Return null for other items since they're already rendered in the FlatList above
              return null;
            }}
            stickySectionHeadersEnabled={false}
            showsVerticalScrollIndicator={false}
            sections={filteredCourseData}
          />
        </YStack>
      </YStack>

      {/* Link Generation Modal for Course Sharing */}
      <LinkGenModal
        ref={linkGenModalRef}
        idOnly
        url={`${HOST}/en/academy/learn/`}
        heading="Share Your Course"
        caption="Generate a shareable link for your course. Anyone with this link can access and learn from your course content."
        embedType="iframe"
      />

      {/* Sort Options Modal with Glass Effect */}
      {showSortModal && (
        <View style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}>
          <Animatable.View
            animation="fadeIn"
            duration={200}
            style={[styles.modalContainer, {
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
              ...(Platform.OS === 'web' && { backdropFilter: 'blur(20px)' }),
            }]}
          >
            <Text style={{
              fontSize: 18,
              fontWeight: '700',
              color: theme ? '#1e293b' : '#f1f5f9',
              marginBottom: 20,
              textAlign: 'center'
            }}>
              Sort Courses By
            </Text>

            <YStack gap={12}>
              {[
                { key: 'upvotes', label: 'Upvotes', icon: 'thumb-up' },
                { key: 'voteScore', label: 'Vote Score', icon: 'chart-line' },
                { key: 'dateAdded', label: 'Date Added', icon: 'calendar' },
                { key: 'title', label: 'Title', icon: 'alphabetical' },
                { key: 'completion', label: 'Completion Status', icon: 'check-circle' },
              ].map((option) => (
                <Pressable
                  key={option.key}
                  onPress={() => {
                    setSortBy(option.key as any);
                    setShowSortModal(false);
                  }}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: 12,
                    borderRadius: 8,
                    backgroundColor: sortBy === option.key
                      ? (theme ? '#dbeafe' : '#1e40af')
                      : (theme ? '#f8fafc' : '#334155'),
                    borderWidth: 1,
                    borderColor: sortBy === option.key
                      ? (theme ? '#3b82f6' : '#60a5fa')
                      : (theme ? '#e2e8f0' : '#475569'),
                  }}
                >
                  <MaterialCommunityIcons
                    name={option.icon as any}
                    size={12}
                    color={sortBy === option.key
                      ? (theme ? '#1e40af' : '#93c5fd')
                      : (theme ? '#64748b' : '#94a3b8')}
                    style={{ marginRight: 12 }}
                  />
                  <Text style={{
                    fontSize: 16,
                    color: sortBy === option.key
                      ? (theme ? '#1e40af' : '#93c5fd')
                      : (theme ? '#1e293b' : '#f1f5f9'),
                    fontWeight: sortBy === option.key ? '600' : '400'
                  }}>
                    {option.label}
                  </Text>
                  {sortBy === option.key && (
                    <MaterialCommunityIcons
                      name="check"
                      size={16}
                      color={theme ? '#1e40af' : '#93c5fd'}
                      style={{ marginLeft: 'auto' }}
                    />
                  )}
                </Pressable>
              ))}
            </YStack>

            <XStack gap={12} marginTop={20}>
              <Pressable
                onPress={() => setShowSortModal(false)}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 8,
                  backgroundColor: theme ? '#f1f5f9' : '#334155',
                  alignItems: 'center'
                }}
              >
                <Text style={{
                  fontSize: 16,
                  color: theme ? '#64748b' : '#94a3b8',
                  fontWeight: '600'
                }}>
                  Cancel
                </Text>
              </Pressable>
              <Pressable
                onPress={() => {
                  setSortBy('upvotes');
                  setSortOrder('desc');
                  setShowSortModal(false);
                }}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 8,
                  backgroundColor: theme ? '#dbeafe' : '#1e40af',
                  alignItems: 'center'
                }}
              >
                <Text style={{
                  fontSize: 16,
                  color: theme ? '#1e40af' : '#93c5fd',
                  fontWeight: '600'
                }}>
                  Reset to Default
                </Text>
              </Pressable>
            </XStack>
          </Animatable.View>
        </View>
      )}
    </DefaultScreen>
  )
}

// Hero-style additional styles
const styles = StyleSheet.create({
  glassCard: Platform.OS === 'web' ? {
    backdropFilter: 'blur(20px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  } : {},
  glassCardSmall: Platform.OS === 'web' ? {
    backdropFilter: 'blur(16px)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
  } : {},
  heroBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    ...(Platform.OS === 'web' && { backdropFilter: 'blur(10px)' }),
  },
  modalContainer: {
    borderRadius: 16,
    padding: 24,
    margin: 20,
    minWidth: 300,
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 10,
  },
});

export default AcademyScreen;